import { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Spin, Typography, message } from 'antd';
import type { InputRef } from 'antd';
import { BarcodeOutlined } from '@ant-design/icons';
import { posService } from '@/services/pos.service';
import { mapDetailToPosVariant } from '../utils/mapVariantDetail';
import { useQrVariantSearch } from '../hooks/useQrVariantSearch';
import { QRSuggestItem } from './QRSuggestItem';
import type { FulfillmentMode, PosVariant, PosVariantSearchItem } from '../types';

interface Props {
  fulfillmentMode: FulfillmentMode;
  onVariantFound: (variant: PosVariant) => void;
}

export function QRSearchBar({ fulfillmentMode, onVariantFound }: Props) {
  const [qr, setQr] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const prevSearchingRef = useRef(false);
  const isAddingRef = useRef(false);

  const { results, isSearching, hasResults } = useQrVariantSearch(qr, fulfillmentMode);

  // Effect 1: open/close results dropdown based on hasResults
  useEffect(() => {
    if (hasResults) {
      setShowResults(true);
      setShowNoResults(false);
    } else {
      setShowResults(false);
    }
  }, [hasResults]);

  // Effect 2: show "no results" once searching transitions to done
  useEffect(() => {
    if (prevSearchingRef.current && !isSearching && !hasResults && qr.trim().length > 0) {
      setShowNoResults(true);
    }
    if (isSearching) setShowNoResults(false);
    prevSearchingRef.current = isSearching;
  }, [isSearching, hasResults, qr]);

  // Effect 3: click outside closes both dropdowns
  useEffect(() => {
    if (!showResults && !showNoResults) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setShowResults(false);
        setShowNoResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showResults, showNoResults]);

  // Effect 4: F3 focus + Escape close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F3') {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape') {
        setShowResults(false);
        setShowNoResults(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const addByQrCode = useCallback(
    async (qrValue: string) => {
      if (isAddingRef.current) return;
      isAddingRef.current = true;
      setIsAdding(true);
      try {
        const { items, item_errors } = await posService.lookupVariants(
          [qrValue],
          fulfillmentMode,
        );
        if (item_errors.length > 0) {
          void message.error(item_errors[0].error);
          return;
        }
        if (items.length === 0) {
          void message.warning('Không tìm thấy sản phẩm');
          return;
        }
        const variant = mapDetailToPosVariant(items[0]);
        onVariantFound(variant);
        setQr('');
        setShowResults(false);
        setShowNoResults(false);
        requestAnimationFrame(() => inputRef.current?.focus());
      } catch {
        void message.error('Lỗi khi lấy thông tin sản phẩm');
      } finally {
        isAddingRef.current = false;
        setIsAdding(false);
      }
    },
    [fulfillmentMode, onVariantFound],
  );

  const selectItem = useCallback(
    async (item: PosVariantSearchItem) => {
      const qrValue = item.serial
        ? `${item.variant.variant_code}_${item.serial}`
        : item.variant.variant_code;
      await addByQrCode(qrValue);
    },
    [addByQrCode],
  );

  const handleEnter = useCallback(async () => {
    if (isAddingRef.current) return;
    const trimmed = qr.trim();
    if (!trimmed) return;

    if (showResults && hasResults) {
      const first = results.serials[0] ?? results.variants[0];
      if (first) {
        await selectItem(first);
        return;
      }
    }
    if (isSearching) {
      void message.info('Đang tìm kiếm, vui lòng đợi…');
      return;
    }
    await addByQrCode(trimmed);
  }, [qr, showResults, hasResults, results, isSearching, selectItem, addByQrCode]);

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1400,
    marginTop: 4,
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 4,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <Input
        ref={inputRef}
        prefix={<BarcodeOutlined />}
        placeholder="QR / Mã SP... (F3)"
        value={qr}
        onChange={(e) => setQr(e.target.value)}
        onPressEnter={() => void handleEnter()}
        suffix={isSearching || isAdding ? <Spin size="small" /> : null}
        allowClear
        autoComplete="off"
      />

      {showNoResults && !hasResults && !isSearching && (
        <div style={{ ...dropdownStyle, padding: '12px 16px' }}>
          <Typography.Text type="secondary">Không tìm thấy sản phẩm phù hợp</Typography.Text>
        </div>
      )}

      {showResults && hasResults && (
        <div
          style={{ ...dropdownStyle, maxHeight: 400, overflow: 'auto' }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.serials.length > 0 && (
            <>
              <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600, fontSize: 13 }}>
                Serials
              </div>
              {results.serials.map((it) => (
                <QRSuggestItem
                  key={`serial-${it.variant.variant_id}-${it.serial ?? ''}`}
                  item={it}
                  onClick={() => void selectItem(it)}
                />
              ))}
            </>
          )}
          {results.variants.length > 0 && (
            <>
              <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600, fontSize: 13 }}>
                Variants
              </div>
              {results.variants.map((it) => (
                <QRSuggestItem
                  key={`variant-${it.variant.variant_id}`}
                  item={it}
                  onClick={() => void selectItem(it)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
